import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  CardBody,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  CardImg,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import DeleteModal from "../../../Components/Common/DeleteModal";
import CampaignService from "../../../services/OrderingServices/CampaignService";
import CampaignAssignService from "../../../services/OrderingServices/CampaignAssignService";
import DeviceHeartbeatService from "../../../services/DeviceHeartbeatServices/DeviceHeartbeatService";
import DeviceAdsService from "../../../services/OrderingServices/DeviceAdsService";
import CampaignFilterCard from "../component/CampaignFilterCard";
import { formatDateTime } from "../../../Components/Common/FormatDate";
import kiosk from "../../../assets/images/kiosk.png";

// Device Card Component for Modal
const DeviceCard = ({ device }) => {
  return (
    <Card
      className="device-card h-100 border-0"
      style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}
    >
      <div className="position-relative">
        <CardImg
          top
          src={kiosk}
          alt={device.machineName}
          style={{
            height: "150px",
            objectFit: "contain",
            background: "#f6f8fa",
          }}
        />
      </div>
      <CardBody className="p-3">
        <h6 className="mb-2 text-truncate fw-semibold">{device.machineName}</h6>
        <div className="small text-muted">
          <div className="mb-1">
            <i className="ri-mac-line me-1" />
            {device.macId}
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

const customStyles = {
  headCells: {
    style: {
      fontSize: "0.92rem",
      fontWeight: 610,
    },
  },
  rows: {
    style: {
      fontSize: "0.9rem",
    },
  },
};

const CampaignsList = () => {
  const navigate = useNavigate();
  const [campaignsList, setCampaignsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [filters, setFilters] = useState({
    name: "",
    isActive: "",
    isDraft: "",
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const [deviceModal, setDeviceModal] = useState(false);
  const [devices, setDevices] = useState([]);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [selectedCampaignForDevices, setSelectedCampaignForDevices] =
    useState(null);
  const [isCloneConfirmOpen, setIsCloneConfirmOpen] = useState(false);
  const [campaignToClone, setCampaignToClone] = useState(null);
  const [isCloning, setIsCloning] = useState(false);

  const toggleDelete = () => setIsOpenDelete(!isOpenDelete);

  const fetchDevicesByCampaign = async (campaign) => {
    try {
      setDeviceLoading(true);
      setSelectedCampaignForDevices(campaign);
      const response = await DeviceHeartbeatService.getDevicesByCampaign(
        campaign.id,
        1,
        100,
        true, // Only active devices
      );

      // Handle paginated response
      if (response && typeof response === "object") {
        setDevices(Array.isArray(response.data) ? response.data : []);
      } else if (Array.isArray(response)) {
        setDevices(response);
      } else {
        setDevices([]);
      }

      setDeviceModal(true);
    } catch (err) {
      toast.error(`Failed to fetch devices for campaign: ${campaign.name}`);
      setDevices([]);
      setDeviceModal(true);
    } finally {
      setDeviceLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    fetchCampaignsList();
  }, [currentPage, pageSize, debouncedFilters]);

  async function fetchCampaignsList(
    page = currentPage,
    size = pageSize,
    currentFilters = debouncedFilters,
  ) {
    try {
      setLoading(true);

      const params = {
        pageNumber: page,
        pageSize: size,
      };

      if (currentFilters.name) {
        params.name = currentFilters.name;
      }

      if (currentFilters.isActive !== "") {
        params.isActive = currentFilters.isActive === "true"; // convert to boolean
      }

      if (currentFilters.isDraft !== "") {
        params.isDraft = currentFilters.isDraft === "true"; // convert to boolean
      }

      const response = await CampaignService.campaignsList(params);

      if (response) {
        let campaignsData = [];
        let totalCount = 0;

        if (response.data && Array.isArray(response.data)) {
          campaignsData = response.data;
          totalCount = response.totalRecords || response.data.length;
        } else if (Array.isArray(response)) {
          campaignsData = response;
          totalCount = response.length;
        } else if (response.value && Array.isArray(response.value)) {
          campaignsData = response.value;
          totalCount = response.totalCount || response.value.length;
        } else if (typeof response === "object") {
          campaignsData = [response];
          totalCount = 1;
        }

        setCampaignsList(campaignsData);
        setTotalRows(totalCount);
      }
    } catch (error) {
      toast.error("Failed to fetch campaigns");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      name: "",
      isActive: "",
      isDraft: "",
    });
    setCurrentPage(1);
  };

  const handleDelete = async () => {
    try {
      if (selectedCampaign && selectedCampaign.id) {
        await CampaignService.delete(selectedCampaign.id);
        toast.success("Campaign deleted successfully");
        toggleDelete();
        fetchCampaignsList();
      }
    } catch (error) {
      toast.error("Failed to delete campaign");
      console.error(error);
    }
  };

  const handleCloneCampaign = (campaign) => {
    setCampaignToClone(campaign);
    setIsCloneConfirmOpen(true);
  };

  const getCampaignStatus = (campaign) => {
    if (campaign.isDraft) {
      return { label: "Draft", bg: "#f1f3f5", color: "#6c757d" };
    }

    const now = new Date();
    const start = campaign.startDate ? new Date(campaign.startDate) : null;
    const end = campaign.endDate ? new Date(campaign.endDate) : null;

    // Not started yet
    if (start && now < start) {
      return { label: "Scheduled", bg: "#fff3cd", color: "#856404" };
    }

    // Started and no end date
    if (start && !end && now >= start) {
      return { label: "Live (Ongoing)", bg: "#e6f7ed", color: "#0f5132" };
    }

    // Normal live window
    if (start && end && now >= start && now <= end) {
      return { label: "Live", bg: "#e6f7ed", color: "#0f5132" };
    }

    // Expired
    if (end && now > end) {
      return { label: "Expired", bg: "#f8d7da", color: "#842029" };
    }

    // Manually inactive
    if (!campaign.isActive) {
      return { label: "Inactive", bg: "#f5f5f5", color: "#6c757d" };
    }

    return { label: "Unknown", bg: "#f5f5f5", color: "#6c757d" };
  };

  const executeCloneCampaign = async () => {
    if (!campaignToClone) return;

    try {
      setIsCloning(true);
      const toastId = toast.loading("Cloning campaign...");

      // Step 1: Fetch original campaign details with ads
      const originalCampaignData = await CampaignService.getCampaignById(
        campaignToClone.id,
      );

      // Step 2: Create new campaign with filtered data
      const newCampaignPayload = {
        name: `${originalCampaignData.name} (Clone)`,
        description: originalCampaignData.description || "",
        startDate: originalCampaignData.startDate,
        endDate: originalCampaignData.endDate,
        playUntilTurnedOff: originalCampaignData.playUntilTurnedOff || false,
        isDraft: true, // Clone as draft
        isActive: false, // Clone as inactive
        tags: originalCampaignData.tags || [],
      };

      const newCampaign = await CampaignService.create(newCampaignPayload);
      // Handle response - API returns either the ID directly or an object with id property
      const newCampaignId =
        typeof newCampaign === "number" ? newCampaign : newCampaign.id;

      // Step 3: Assign ads from original campaign to new campaign
      if (originalCampaignData.ads && originalCampaignData.ads.length > 0) {
        const adIds = originalCampaignData.ads.map((ad) => ad.id);

        await CampaignAssignService.assignAdsToCampaigns({
          campaignIds: [newCampaignId],
          adIds: adIds,
        });
      }

      // Step 4: Get devices assigned to original campaign
      const devicesResponse = await DeviceHeartbeatService.getDevicesByCampaign(
        campaignToClone.id,
        1,
        1000, // Get all devices
        false,
      );

      // Step 5: Bulk assign new campaign to all devices
      if (devicesResponse && devicesResponse.data) {
        const devices = Array.isArray(devicesResponse.data)
          ? devicesResponse.data
          : [];

        if (devices.length > 0) {
          const bulkAssignPayload = devices.map((device) => ({
            macId: device.macId,
            campaignId: newCampaignId,
            adId: null, // Will be handled after assignment
            orderIndex: 0,
          }));

          await DeviceAdsService.bulkAssignCampaigns(bulkAssignPayload);
        }
      }

      toast.dismiss(toastId);
      toast.success("Campaign cloned successfully!");
      setIsCloneConfirmOpen(false);
      setCampaignToClone(null);
      fetchCampaignsList();
    } catch (error) {
      console.error("Error cloning campaign:", error);
      toast.error(
        error.message || "Failed to clone campaign. Please try again.",
      );
    } finally {
      setIsCloning(false);
    }
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (currentPage - 1) * pageSize + index + 1,
      sortable: true,
      width: "80px",
    },
    {
      name: "Campaign Name",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <span
          className="text-primary"
          style={{
            // color: "#1c1c1c",
            cursor: "pointer",
            // textDecoration: "underline",
            fontWeight: 500,
          }}
          onClick={() => navigate(`/campaign/view/${row.id}`)}
        >
          {row.name}
        </span>
      ),
    },
    // {
    //   name: "Description",
    //   selector: (row) => row.description || "—",
    //   sortable: true,
    //   //   width: "250px",
    //   cell: (row) => (
    //     <div
    //       style={{
    //         overflow: "hidden",
    //         textOverflow: "ellipsis",
    //         whiteSpace: "nowrap",
    //         maxWidth: "250px",
    //       }}
    //       title={row.description}
    //     >
    //       {row.description || "—"}
    //     </div>
    //   ),
    // },
    {
      name: "Start Date",
      selector: (row) => (row.startDate ? formatDateTime(row.startDate) : "—"),
      sortable: true,
      //   width: "150px",
    },
    {
      name: "End Date",
      selector: (row) => (row.endDate ? formatDateTime(row.endDate) : "—"),
      sortable: true,
      //   width: "150px",
    },
    {
      name: "Status",
      sortable: true,
      cell: (row) => {
        const status = getCampaignStatus(row);

        return (
          <span
            className="badge"
            style={{
              backgroundColor: status.bg,
              color: status.color,
              fontWeight: 500,
              padding: "4px 10px",
              fontSize: "0.85rem",
            }}
          >
            {status.label}
          </span>
        );
      },
    },
    {
      name: "Ads Count",
      selector: (row) => row?.adsCount || 0,
      sortable: true,
      //   width: "80px",
      center: true,
    },
    {
      name: "Action",
      selector: (row) => (
        <div className="d-flex gap-2">
          <Button
            color="btn btn-soft-info"
            className="btn-sm"
            onClick={() => navigate(`/campaign/view/${row.id}`)}
            title="View Details"
          >
            <i className="ri-eye-line" />
          </Button>
          <Button
            color="btn btn-soft-primary"
            className="btn-sm"
            onClick={() => navigate(`/campaign/${row.id}`)}
            title="Edit"
          >
            <i className="ri-edit-line" />
          </Button>
          <Button
            color="btn btn-soft-primary"
            className="btn-sm"
            onClick={() => {
              setSelectedCampaign(row);
              handleCloneCampaign(row); // create this function
            }}
            title="Clone Campaign"
          >
            <i className="ri-file-copy-line" />
          </Button>
          <Button
            color="btn btn-soft-danger"
            className="btn-sm"
            onClick={() => {
              setSelectedCampaign(row);
              setIsOpenDelete(true);
            }}
            title="Delete"
          >
            <i className="ri-delete-bin-line" />
          </Button>
        </div>
      ),
      center: true,
      // width: "140px",
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <BreadCrumb title="Campaign List" pageTitle="Campaigns" />
        </div>
        <Container fluid>
          {/* Filter Card */}
          <CampaignFilterCard
            filters={filters}
            handleFilterChange={handleFilterChange}
            handleClearFilters={handleClearFilters}
            setCurrentPage={setCurrentPage}
            onAddNewClick={() => navigate("/campaign/new")}
          />

          {/* Data Table */}
          <Card className="border-0 shadow-sm">
            <CardBody>
              <CustomDataTable
                responsive
                striped
                pagination
                fixedHeader
                persistTableHead
                paginationServer
                paginationTotalRows={totalRows}
                onChangePage={(page) => setCurrentPage(page)}
                onChangeRowsPerPage={(newSize, page) => {
                  setPageSize(newSize);
                  setCurrentPage(1);
                }}
                progressPending={loading}
                customStyles={customStyles}
                progressComponent={
                  <div className="my-3 text-center">
                    <Triangle
                      visible={true}
                      height="80"
                      width="80"
                      color="#435ebe"
                      ariaLabel="triangle-loading"
                    />
                    <h5 className="mt-2">Loading...</h5>
                  </div>
                }
                columns={columns}
                data={campaignsList}
                noDataComponent={
                  <div className="py-4 text-center">
                    <p className="text-muted">No campaigns found</p>
                  </div>
                }
              />
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* Device Modal */}
      <Modal
        isOpen={deviceModal}
        toggle={() => setDeviceModal(false)}
        size="lg"
        centered
        contentClassName="border-0 shadow-lg"
      >
        <ModalHeader
          className="bg-light border-bottom"
          toggle={() => setDeviceModal(false)}
          style={{ padding: "20px 24px" }}
        >
          <div className="d-flex align-items-center gap-2">
            <i
              className="ri-device-line"
              style={{ fontSize: "20px", color: "#0dcaf0" }}
            ></i>
            <div>
              <h6 className="mb-0">Devices in Campaign</h6>
              <small className="text-muted">
                {selectedCampaignForDevices?.name || "Campaign"}
              </small>
            </div>
          </div>
        </ModalHeader>
        <ModalBody style={{ padding: "24px" }}>
          {deviceLoading && (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Triangle
                height="80"
                width="80"
                color="#0dcaf0"
                ariaLabel="triangle-loading"
                visible={true}
              />
            </div>
          )}

          {!deviceLoading && devices.length === 0 && (
            <div className="alert alert-info" role="alert">
              <i className="ri-information-line me-2"></i>
              No active devices assigned to this campaign.
            </div>
          )}

          {!deviceLoading && devices.length > 0 && (
            <Row className="g-3">
              {devices.map((device) => (
                <Col key={device.macId} xs="12" sm="6" md="4" lg="3">
                  <DeviceCard device={device} />
                </Col>
              ))}
            </Row>
          )}
        </ModalBody>
      </Modal>

      {/* Delete Modal */}
      <DeleteModal
        isOpen={isOpenDelete}
        toggle={toggleDelete}
        onConfirm={handleDelete}
        title="Delete Campaign"
        body={`Are you sure you want to delete "${selectedCampaign?.name}"?`}
      />

      {/* Clone Confirmation Modal */}
      <Modal
        isOpen={isCloneConfirmOpen}
        toggle={() => !isCloning && setIsCloneConfirmOpen(false)}
        centered
        contentClassName="border-0 shadow-lg"
      >
        <ModalHeader
          toggle={() => !isCloning && setIsCloneConfirmOpen(false)}
          className="bg-light border-bottom"
        >
          <div className="d-flex align-items-center gap-2">
            <i
              className="ri-file-copy-line"
              style={{ fontSize: "20px", color: "#435ebe" }}
            ></i>
            <h6 className="mb-0">Clone Campaign</h6>
          </div>
        </ModalHeader>
        <ModalBody className="py-4">
          <div className="mb-3">
            <p className="mb-2 fw-semibold text-dark">
              Do you want to clone this campaign?
            </p>
            <p className="text-muted mb-0">
              Campaign: <strong>{campaignToClone?.name}</strong>
            </p>
          </div>
          <div className="alert alert-info mb-0" role="alert">
            <i className="ri-information-line me-2"></i>
            <span className="small">
              A new campaign will be created with all ads and device assignments
              from the original campaign. The new campaign will be saved as a
              draft.
            </span>
          </div>
        </ModalBody>
        <ModalFooter className="border-top bg-light">
          <Button
            color="secondary"
            onClick={() => setIsCloneConfirmOpen(false)}
            disabled={isCloning}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={executeCloneCampaign}
            disabled={isCloning}
          >
            {isCloning ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Cloning...
              </>
            ) : (
              <>
                <i className="ri-file-copy-line me-2"></i>Clone Campaign
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default CampaignsList;
